import { useEffect } from 'react';

/**
 * Re-run a fetch when the app comes back into use.
 *
 * Most screens read Dexie through `useLiveQuery` and update themselves. The two
 * that hold server state instead — the activity feed and the agenda change log
 * — take a snapshot when they mount and then keep showing it. On a phone that
 * is the normal case,
 * not an edge one: a PWA is left open in the background for hours and resumed,
 * so "when it mounted" can easily mean yesterday.
 *
 * The triggers are deliberately the same ones the sync loop uses. A screen that
 * refreshed on different signals from the data underneath it would be a second
 * answer to "is this current?", and the two would disagree.
 */
export function useRefreshOnReturn(refresh: () => void | Promise<void>): void {
  useEffect(() => {
    const run = () => void refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') run();
    };

    window.addEventListener('online', run);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', run);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // The caller owns the identity of `refresh`; re-subscribing on every render
    // would add and drop listeners continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
